import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import BrandLogo from '../../components/common/BrandLogo';
import { MdEmail, MdLock, MdPerson, MdPhone, MdCheckCircle } from 'react-icons/md';
import Input from '../../components/common/Input';
import Button from '../../components/common/Button';
import { register as registerApi, checkEmail } from '../../services/authService';
import { BELT_ORDER } from '../../constants/belts';

const DISCIPLINES = ['Taekwondo','Kickboxing','Karate','Judo','MMA','Boxing'];

const STEPS = ['Account', 'Personal', 'Sport'];

const PasswordStrength = ({ password }) => {
  if (!password) return null;
  let strength = 0;
  if (password.length >= 8)     strength++;
  if (/[A-Z]/.test(password))  strength++;
  if (/[0-9]/.test(password))  strength++;
  if (/[^A-Za-z0-9]/.test(password)) strength++;

  const labels = ['', 'Weak', 'Fair', 'Good', 'Strong'];
  const colors  = ['', 'bg-red-400', 'bg-yellow-400', 'bg-blue-400', 'bg-green-500'];
  const widths  = ['', 'w-1/4', 'w-2/4', 'w-3/4', 'w-full'];
  const texts   = ['', 'text-red-500', 'text-yellow-500', 'text-blue-500', 'text-green-600'];

  return (
    <div className="mt-1">
      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all duration-300 ${colors[strength]} ${widths[strength]}`} />
      </div>
      {strength > 0 && (
        <p className={`text-xs mt-0.5 font-medium ${texts[strength]}`}>{labels[strength]}</p>
      )}
    </div>
  );
};

function StudentSignup() {
  const [step, setStep]         = useState(0);
  const [loading, setLoading]   = useState(false);
  const [done, setDone]         = useState(null);
  const navigate                = useNavigate();

  const { register, handleSubmit, watch, trigger, setError, formState: { errors } } = useForm({
    defaultValues: { role: 'student' },
  });

  const password = watch('password');

  const nextStep = async () => {
    const fieldsPerStep = [
      ['name', 'email', 'password', 'confirmPassword'],
      ['phone'],
      [],
    ];
    const valid = await trigger(fieldsPerStep[step]);
    if (!valid) return;

    if (step === 0) {
      const { data } = await checkEmail(watch('email')).catch(() => ({ data: { available: true } }));
      if (!data.available) {
        setError('email', { message: 'This email is already registered' });
        return;
      }
    }

    setStep(s => s + 1);
  };

  const onSubmit = async (values) => {
    setLoading(true);
    try {
      const { confirmPassword, ...payload } = values;
      const { data } = await registerApi({ ...payload, role: 'student' });

      if (data.token) {
        // Demo mode — auto logged in
        navigate('/student/dashboard', { replace: true });
        toast.success('Welcome to TrainAxis!');
      } else {
        setDone(data);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-[#141312] flex items-center justify-center px-4">
        <div className="bg-[#1e1d1b] rounded-3xl border border-[rgba(237,232,224,0.08)] shadow-2xl p-8 max-w-sm w-full text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <MdCheckCircle className="text-green-500 text-4xl" />
          </div>
          <h2 className="text-[#ede8e0] font-bold text-xl mb-2">You're registered!</h2>
          <p className="text-[#8a847c] text-sm mb-6">{done.message}</p>
          <Link to="/login">
            <Button fullWidth>Go to Login</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#141312] flex flex-col">
      {/* Header */}
      <div className="bg-primary pt-10 pb-20 relative overflow-hidden">
        <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-primary-dark opacity-40" />
        <div className="relative z-10 flex justify-center mb-4">
          <div className="flex items-center gap-2.5 bg-white/20 backdrop-blur-sm px-5 py-2.5 rounded-2xl border border-white/20">
            <BrandLogo size={26} />
            <span className="text-white font-bold text-lg">TrainAxis</span>
          </div>
        </div>
        <div className="relative z-10 text-center px-6">
          <h1 className="text-white text-xl font-bold">Student Registration</h1>
          {/* Step indicator */}
          <div className="flex items-center justify-center gap-2 mt-4">
            {STEPS.map((label, i) => (
              <div key={i} className="flex items-center gap-2">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all
                  ${i < step ? 'bg-white text-primary' : i === step ? 'bg-white text-primary ring-2 ring-white ring-offset-2 ring-offset-primary' : 'bg-white/30 text-white'}`}>
                  {i < step ? '✓' : i + 1}
                </div>
                <span className={`text-xs font-medium ${i === step ? 'text-white' : 'text-white/60'}`}>{label}</span>
                {i < STEPS.length - 1 && <div className={`w-6 h-0.5 ${i < step ? 'bg-white' : 'bg-white/30'}`} />}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="relative z-10 -mt-10 px-4 pb-10 max-w-sm w-full mx-auto">
        <div className="bg-[#1e1d1b] rounded-3xl border border-[rgba(237,232,224,0.08)] shadow-2xl p-6">
          <form onSubmit={handleSubmit(onSubmit)} noValidate>

            {/* Step 0 — Account */}
            {step === 0 && (
              <div className="space-y-4">
                <h3 className="font-bold text-[#ede8e0] text-base mb-1">Account Details</h3>
                <Input
                  icon={MdPerson}
                  placeholder="Full name"
                  error={errors.name?.message}
                  {...register('name', {
                    required: 'Name is required',
                    minLength: { value: 2, message: 'At least 2 characters' },
                  })}
                />
                <Input
                  icon={MdEmail}
                  type="email"
                  placeholder="Email address"
                  error={errors.email?.message}
                  {...register('email', {
                    required: 'Email is required',
                    pattern: { value: /^\S+@\S+\.\S+$/, message: 'Enter a valid email' },
                  })}
                />
                <div>
                  <Input
                    icon={MdLock}
                    type="password"
                    placeholder="Password (min 8 chars)"
                    error={errors.password?.message}
                    {...register('password', {
                      required: 'Password is required',
                      minLength: { value: 8, message: 'At least 8 characters' },
                      validate: v => /[A-Z]/.test(v) || 'Must contain an uppercase letter',
                    })}
                  />
                  <PasswordStrength password={password} />
                </div>
                <Input
                  icon={MdLock}
                  type="password"
                  placeholder="Confirm password"
                  error={errors.confirmPassword?.message}
                  {...register('confirmPassword', {
                    required: 'Please confirm your password',
                    validate: v => v === password || 'Passwords do not match',
                  })}
                />
                <Button type="button" fullWidth onClick={nextStep}>Continue</Button>
              </div>
            )}

            {/* Step 1 — Personal */}
            {step === 1 && (
              <div className="space-y-4">
                <h3 className="font-bold text-[#ede8e0] text-base mb-1">Personal Details</h3>
                <Input
                  icon={MdPhone}
                  type="tel"
                  placeholder="Phone (e.g. 9801234567)"
                  maxLength={10}
                  error={errors.phone?.message}
                  {...register('phone', {
                    pattern: { value: /^(97|98)\d{8}$/, message: 'Enter a valid Nepal mobile number (97xx/98xx)' },
                  })}
                />
                <div>
                  <label className="block text-xs font-medium text-[#8a847c] mb-1">Date of Birth</label>
                  <input type="date" className="w-full bg-[#1a1918] border border-[rgba(237,232,224,0.08)] rounded-xl px-3 py-2.5 text-sm text-[#ede8e0] placeholder-[#6b6560] focus:outline-none focus:border-[rgba(237,232,224,0.15)]"
                    {...register('dateOfBirth')} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#8a847c] mb-1">Gender</label>
                  <select className="w-full bg-[#1a1918] border border-[rgba(237,232,224,0.08)] rounded-xl px-3 py-2.5 text-sm text-[#ede8e0] placeholder-[#6b6560] focus:outline-none focus:border-[rgba(237,232,224,0.15)]"
                    {...register('gender')}>
                    <option value="">Select gender</option>
                    <option>Male</option>
                    <option>Female</option>
                    <option>Other</option>
                  </select>
                </div>
                <Input placeholder="Address (optional)" {...register('address')} />
                <div className="flex gap-2">
                  <Button type="button" variant="outline" fullWidth onClick={() => setStep(0)}>Back</Button>
                  <Button type="button" fullWidth onClick={nextStep}>Continue</Button>
                </div>
              </div>
            )}

            {/* Step 2 — Sport */}
            {step === 2 && (
              <div className="space-y-4">
                <h3 className="font-bold text-[#ede8e0] text-base mb-1">Martial Arts Info</h3>
                <div>
                  <label className="block text-xs font-medium text-[#8a847c] mb-1">Discipline</label>
                  <select className="w-full bg-[#1a1918] border border-[rgba(237,232,224,0.08)] rounded-xl px-3 py-2.5 text-sm text-[#ede8e0] placeholder-[#6b6560] focus:outline-none focus:border-[rgba(237,232,224,0.15)]"
                    {...register('discipline')}>
                    {DISCIPLINES.map(d => <option key={d}>{d}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#8a847c] mb-1">Current Belt</label>
                  <select className="w-full bg-[#1a1918] border border-[rgba(237,232,224,0.08)] rounded-xl px-3 py-2.5 text-sm text-[#ede8e0] placeholder-[#6b6560] focus:outline-none focus:border-[rgba(237,232,224,0.15)]"
                    {...register('currentGrade')}>
                    {BELT_ORDER.map(b => <option key={b}>{b}</option>)}
                  </select>
                </div>
                <Input placeholder="Emergency contact name (optional)" {...register('emergencyContactName')} />
                <Input
                  icon={MdPhone}
                  type="tel"
                  placeholder="Emergency contact phone"
                  maxLength={10}
                  error={errors.emergencyContactPhone?.message}
                  {...register('emergencyContactPhone', { pattern: { value: /^(97|98)\d{8}$/, message: 'Enter a valid Nepal mobile number' } })}
                />
                <div className="flex gap-2">
                  <Button type="button" variant="outline" fullWidth onClick={() => setStep(1)}>Back</Button>
                  <Button type="submit" fullWidth loading={loading}>Create Account</Button>
                </div>
              </div>
            )}

          </form>

          <p className="text-center text-sm text-[#8a847c] mt-4">
            Already have an account?{' '}
            <Link to="/login" className="text-primary font-semibold hover:underline">Log in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default StudentSignup;
